import { Hero, HeroItem, UploadedFile } from '../../../domain/hero';
import {
  CreateHeroRequestApiModel,
  CreatePlatformImageRequestApiModel,
  CreateSpotlightRequestApiModel,
  LinearSpotlightItemApiModel,
  OndemandSpotlightItemApiModel,
  SpotlightItemTypeApiModel,
  UpdateHeroRequestApiModel,
  UpdateSpotlightPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { HeroSpotlight, HeroSpotlightItem } from '../../../domain/hero-spotlight';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import { nameLocalizedMapperDomainToApi, targetingMapperDomainToApi } from '../../mapping/create-tray-domain-to-api';
import { HeroDetailApiModel } from '@cxb/editor-stb-api/model/models';
import { HeroLayout } from '../../../domain/domain-layouts';
import {
  commonMapper,
  mapApiModelImageToDomain,
  nameLocalizedMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import {
  HeroTrayFormViewModel,
  PartialHeroItem,
  SpotlightDetailWithProgramApiModel,
  SpotlightItemWithProgramApiModel,
} from './components/hero-tray-form/hero-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { HeroSpotlightTrayFormViewModel } from './components/hero-spotlight-tray-form/hero-spotlight-tray-form.view-model';
import {
  commonMapperDomainToViewModel,
  nameLocalizedMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { HeroSpotlightInputViewModel } from './components/hero-spotlight-input/hero-spotlight-input.view-model';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'Hero'
// 'HeroSpotlight'

/**
 * DOMAIN -> API
 */

export const mapHeroTrayDomainModelToApiModel = (
  hero: Hero,
  pageVersionId?: string,
  tenantId?: string
): CreateHeroRequestApiModel & UpdateHeroRequestApiModel => {
  const mapImage = (image: UploadedFile): CreatePlatformImageRequestApiModel => {
    return (
      image && {
        imageId: image.id,
        platformId: image.platformId,
      }
    );
  };
  return {
    // TODO: Design has no fields of this nameLocalized (name of the hero)
    pageVersionId,
    nameLocalized: { en: hero.titleEnglish || 'Hero', ar: hero.titleArabic || 'Hero' },
    userSegmentIds: hero.userSegmentIds,
    heroItems: hero.heroItems.map((heroItem) => ({
      contentId: heroItem.program.id.toString(),
      ribbonTextLocalized: {
        // TODO: BE should accept empty marketingMessage (see CreateHeroItemRequestApiModel definition)
        en: heroItem.marketingMessageEnglish || '',
        ar: heroItem.marketingMessageArabic || '',
      },
      order: heroItem.slot,
      scheduledFrom: heroItem.scheduledFrom,
      scheduledTo: heroItem.scheduledTo,
      images: [heroItem.webImage, heroItem.tabletImage, heroItem.mobileImage, heroItem.cotvImage].map(mapImage).filter((image) => !!image),
    })),
    platformIds: hero.platformIds,
    tenantId,
  };
};

export const mapHeroSpotlightTrayDomainModelToApiModel = (
  item: HeroSpotlight,
  pageVersionId?: string,
  tenantId?: string
): CreateSpotlightRequestApiModel & UpdateSpotlightPageItemRequestApiModel => {
  const commonProperties = mapperPipe(item, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi);

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    spotlightItems: item.heroItems.map((heroItem: HeroSpotlightItem) => {
      const getHeroItemDetailsByContentType = (contentType) => {
        switch (contentType) {
          case SpotlightItemTypeApiModel.StoreSpotlightItemApiModel:
          case SpotlightItemTypeApiModel.VodSpotlightItemApiModel:
            return {
              contentId: `${heroItem.program.id}`,
              programType: heroItem.program.programType,
            };
          case SpotlightItemTypeApiModel.CurrentlyOnTvSpotlightItemApiModel:
            return {
              channelId: heroItem.channel.id,
            };
          case SpotlightItemTypeApiModel.LinearSpotlightItemApiModel:
            return {
              channelId: heroItem.channel.id,
              startTime: new Date(heroItem.programStartTime).getTime(),
              endTime: new Date(heroItem.programEndTime).getTime(),
              programId: heroItem.programId,
              instanceId: heroItem.instanceId,
            };
          case SpotlightItemTypeApiModel.OndemandSpotlightItemApiModel:
            return {
              contentId: `${heroItem.program.id}`,
              programType: heroItem.program.programType,
              previewUrl: heroItem.autoPreview?.publicUrl,
              previewGuid: heroItem.autoPreview?.guid,
            };
          default:
            return {};
        }
      };

      return {
        id: heroItem.id,
        order: heroItem.slot,
        spotlightItemType: heroItem.contentType,
        displayServiceTypeIcon: heroItem.displayServiceTypeIcon,
        marketingMessageLocalized: {
          en: heroItem.hasMarketingMessage ? heroItem.marketingMessageEnglish : '',
          ar: heroItem.hasMarketingMessage ? heroItem.marketingMessageArabic : '',
        },
        scheduledFrom: heroItem.scheduledFrom,
        scheduledTo: heroItem.scheduledTo,
        image: heroItem.cotvImage?.id,
        ...getHeroItemDetailsByContentType(heroItem.contentType),
      };
    }),
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapHeroTrayApiModelToDomainModel = (hero: HeroDetailApiModel): Hero => {
  return {
    id: hero.id,
    titleEnglish: hero.nameLocalized.en,
    titleArabic: hero.nameLocalized.ar,
    userSegmentIds: hero.userSegments.map((userSegment) => userSegment.id),
    platformIds: hero.platforms.map((pl) => pl.id),
    order: undefined,
    draggable: false,
    type: HeroLayout.HERO,
    visible: true,
    heroItems: hero.heroItems.map(
      // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
      (item): HeroItem => {
        const {
          id,
          contentId,
          ribbonTextLocalized,
          order: slot,
          scheduledFrom,
          scheduledTo,
          images = [],
          title,
          availabilityFrom,
          availabilityTo,
        } = item;
        const { en: marketingMessageEnglish, ar: marketingMessageArabic } = ribbonTextLocalized ?? {};
        const webImage = images.find((image) => image.platform.name === PlatformType.Web);
        const tabletImage = images.find((image) => image.platform.name === PlatformType.Tablet);
        const mobileImage = images.find((image) => image.platform.name === PlatformType.Mobile);
        const cotvImage = images.find((image) => image.platform.name === PlatformType.BigScreen);
        return {
          id,
          program: {
            id: parseInt(contentId, 10), // TODO: Inconsistent BE. In HeroDetailApiModel it's a string, in ProgramApiModel it is number.
            title,
            availabilityFrom,
            availabilityTo,
          },
          hasMarketingMessage: Boolean(marketingMessageArabic || marketingMessageEnglish),
          marketingMessageArabic,
          marketingMessageEnglish,
          slot,
          scheduledFrom,
          scheduledTo,
          webImage: webImage && mapApiModelImageToDomain(webImage),
          tabletImage: tabletImage && mapApiModelImageToDomain(tabletImage),
          mobileImage: mobileImage && mapApiModelImageToDomain(mobileImage),
          cotvImage: cotvImage && mapApiModelImageToDomain(cotvImage),
        };
      }
    ),
  };
};

export const mapHeroSpotlightTrayApiModelToDomainModel = (heroSpotlightPageItem: SpotlightDetailWithProgramApiModel): HeroSpotlight => {
  const mapperTransformedSpotlightItems = remainderMapper(
    mapperPipe(heroSpotlightPageItem, commonMapper, nameLocalizedMapperApiToDomain, targetingMapperApiToDomain),
    // eslint-disable-next-line sonarjs/cognitive-complexity
    () => ({
      type: 'HeroSpotlight',
      heroItems: heroSpotlightPageItem.spotlightItems.map((spotlightItem: SpotlightItemWithProgramApiModel) => {
        const linearSpotlightItem: LinearSpotlightItemApiModel = spotlightItem;
        const onDemandSpotlightItem: OndemandSpotlightItemApiModel = spotlightItem;

        const {
          id,
          order,
          spotlightItemType,
          scheduledFrom,
          scheduledTo,
          marketingMessageLocalized,
          displayServiceTypeIcon,
          image,
          program,
        } = spotlightItem;

        return {
          id,
          slot: order,
          contentType: spotlightItemType,
          scheduledFrom,
          scheduledTo,
          hasMarketingMessage: !!marketingMessageLocalized?.en || !!marketingMessageLocalized?.ar,
          marketingMessageEnglish: marketingMessageLocalized?.en,
          marketingMessageArabic: marketingMessageLocalized?.ar,
          displayServiceTypeIcon,
          cotvImage: image
            ? {
                id: image.id,
                name: image.originalName,
                publicUrl: image.publicUrl,
              }
            : undefined,
          programStartTime:
            spotlightItemType === SpotlightItemTypeApiModel.LinearSpotlightItemApiModel
              ? (linearSpotlightItem.startTime && new Date(linearSpotlightItem.startTime).getTime()) || undefined
              : undefined,
          programEndTime:
            spotlightItemType === SpotlightItemTypeApiModel.LinearSpotlightItemApiModel
              ? (linearSpotlightItem.endTime && new Date(linearSpotlightItem.endTime).getTime()) || undefined
              : undefined,
          programId:
            spotlightItemType === SpotlightItemTypeApiModel.LinearSpotlightItemApiModel
              ? linearSpotlightItem.programId || undefined
              : undefined,
          instanceId:
            spotlightItemType === SpotlightItemTypeApiModel.LinearSpotlightItemApiModel
              ? linearSpotlightItem.instanceId || undefined
              : undefined,
          channel:
            spotlightItemType === SpotlightItemTypeApiModel.LinearSpotlightItemApiModel ||
            spotlightItemType === SpotlightItemTypeApiModel.CurrentlyOnTvSpotlightItemApiModel
              ? {
                  id: linearSpotlightItem.channel.id,
                  channelID: linearSpotlightItem.channel.channelId,
                  channelName: linearSpotlightItem.channel.channelName,
                  channelNumber: linearSpotlightItem.channel.channelNumber,
                }
              : undefined,
          program:
            spotlightItemType === SpotlightItemTypeApiModel.OndemandSpotlightItemApiModel ||
            spotlightItemType === SpotlightItemTypeApiModel.VodSpotlightItemApiModel ||
            spotlightItemType === SpotlightItemTypeApiModel.StoreSpotlightItemApiModel
              ? program
              : undefined,
          ...(spotlightItemType === SpotlightItemTypeApiModel.OndemandSpotlightItemApiModel && {
            autoPreview: {
              guid: onDemandSpotlightItem.previewGuid,
              publicUrl: onDemandSpotlightItem.previewUrl,
              title: program.title,
            },
          }),
        };
      }),
    })
  );
  return mapperTransformedSpotlightItems as HeroSpotlight;
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapHeroTrayDomainModelToViewModel = (hero: Hero): HeroTrayFormViewModel => {
  return {
    id: hero.id,
    titleEnglish: hero.titleEnglish,
    heroItems: hero.heroItems as PartialHeroItem[],
    userSegments: hero.userSegmentIds,
    platformIds: hero.platformIds,
    disabled: false,
    selected: undefined,
    type: TrayFormViewModelType.HeroTray,
  };
};

export const mapHeroSpotlightTrayDomainModelToViewModel = (heroSpotlightItem: HeroSpotlight): HeroSpotlightTrayFormViewModel => {
  const commonProperties = mapperPipe(
    heroSpotlightItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.HeroSpotlightTray,
    heroItems: heroSpotlightItem.heroItems.map((heroItem) => {
      return {
        id: heroItem.id,
        scheduledFrom: heroItem.scheduledFrom,
        scheduledTo: heroItem.scheduledTo,
        hasMarketingMessage: heroItem.hasMarketingMessage,
        marketingMessageEnglish: heroItem.marketingMessageEnglish,
        marketingMessageArabic: heroItem.marketingMessageArabic,
        displayServiceTypeIcon: heroItem.displayServiceTypeIcon,
        slot: heroItem.slot,
        valid: true,
        contentType: heroItem.contentType,
        channel: heroItem.channel,
        program: heroItem.program,
        programId: heroItem.programId,
        instanceId: heroItem.instanceId,
        programStartTime: heroItem.programStartTime,
        programEndTime: heroItem.programEndTime,
        cotvImage: heroItem.cotvImage,
        autoPreview: heroItem.autoPreview,
      };
    }),
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapHeroTrayViewModelToDomainModel = (hero: HeroTrayFormViewModel, platformIds: Record<PlatformType, string>): Hero => {
  return {
    id: hero.id,
    type: 'Hero',
    userSegmentIds: hero.userSegments,
    platformIds: hero.platformIds,
    heroItems:
      (hero.heroItems as HeroItem[])?.map((heroItem) => ({
        ...heroItem,
        webImage: { ...heroItem.webImage, platformId: platformIds[PlatformType.Web] },
        mobileImage: { ...heroItem.mobileImage, platformId: platformIds[PlatformType.Mobile] },
        tabletImage: { ...heroItem.tabletImage, platformId: platformIds[PlatformType.Tablet] },
        cotvImage: { ...heroItem.cotvImage, platformId: platformIds[PlatformType.BigScreen] },
      })) ?? [],
    titleEnglish: 'Hero',
    order: undefined,
    visible: undefined,
    draggable: false,
  };
};

export const mapHeroSpotlightTrayViewModelToDomainModel = (
  vm: HeroSpotlightTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): HeroSpotlight => {
  const commonProperties = mapperPipe(vm, commonMapperViewModelToDomain, nameLocalizedMapperViewModelToDomain, targetingMapper);

  return remainderMapper(commonProperties, () => ({
    type: 'HeroSpotlight',
    heroItems:
      (vm.heroItems as HeroSpotlightInputViewModel[])?.map((heroItem) => ({
        id: heroItem.id,
        program: heroItem.program,
        slot: heroItem.slot,
        contentType: heroItem.contentType,
        channel: heroItem.channel,
        scheduledFrom: heroItem.scheduledFrom,
        scheduledTo: heroItem.scheduledTo,
        programStartTime: heroItem.programStartTime,
        programEndTime: heroItem.programEndTime,
        programId: heroItem.programId,
        instanceId: heroItem.instanceId,
        hasMarketingMessage: heroItem.hasMarketingMessage,
        marketingMessageArabic: heroItem.marketingMessageArabic,
        marketingMessageEnglish: heroItem.marketingMessageEnglish,
        displayServiceTypeIcon: heroItem.displayServiceTypeIcon,
        cotvImage: { ...heroItem.cotvImage, platformId: platformIds[PlatformType.BigScreen] },
        autoPreview: heroItem.autoPreview,
      })) ?? [],
  }));
};
